import _ from 'lodash';
import { DateTime } from 'luxon';

const certUtils = {
  getLatestCert(certs) {
    return _.orderBy(certs, [item => item.updated], ['desc'])[0]
  }
  , findCompletedCert(certs) {
    // for a list of certs, return the completed one
    // NOTES:
    // a user should only ever have ONE status==completed cert for a given training module
    // HOWEVER, they can have more than one "valid" cert for the same module
    // this is because in the 60 days leading up to a cert becoming expired, the user can
    // retake the cert again. the 60 days up to expiry has a separate status, though.
    // in this method, we should always return the completed cert first, and only
    // return the "soon to be expired" cert if completed doesnt exist
    let completedCert;
    let validCert;
    for(let cert of certs) {
      if(cert.status == "completed" || cert.status == "exempt") {
        completedCert = cert;
      } else if(this.checkIfValid(cert, null)) {
        validCert = cert;
      }
    }
    return completedCert ? completedCert : validCert;
  }
  , checkIfValid(cert, contentModule) {
    // if making any changes, also change the same util in the server/moduleCertificatesController
    if(!cert) {
      return false;
    } else if(cert.status == "completed" || cert.status == "exempt") {
      // "completed" statuses
      return true;
    } else if(cert.status == "expiring-soon") {
      // TBD on actual status, but might be worth having its own check
      return true;
    } else if(cert.status == "excluded") {
      // 1.12 role based training
      return true;
    } else {
      // all other statuses are negative
      return false;
    }
  }

  , checkIfValidOLD(cert, contentModule) {
    // DEPRECIATED
    /**
     * UPDATES 1.6.2: moduleCertificate.expires is the source of truth here. 
     * Any subsequent updates to tm.certificateDuration mean do not backwards
     * effect pre-existing module certs
     * 
     * DEPRECIATED:
     * NOTE: !tm.certificateDuration means certs should never expire
     */
    if(!cert) {
      // no cert present.. invalid by default
      return false;
    } else if(cert.status !== 'completed' && cert.status !== 'exempt') {
      // 1.6.2: this should be the only check that matters going forward
      // TODO: also add "expiring soon" status here
      
      // cert status is something other than 'completed' (i.e. 'revoked' or 'invalidated')
      return false;
    // } else if(contentModule && !contentModule.certificateDuration) {
    //   /**
    //    * the training module itself is telling us that the cert should never expire.
    //    * So long as status is 'completed' then this is a valid cert regardless.
    //    */
    //   return true;
    } else if(cert && !cert.expires) {
      /**
       * cert has no expiration date for some reason (probably manually added),
       * so it should be valid.
       */
      return true;
    } else {
      /**
       * cert expires AND the training module has a certificateDuration of some length,
       * so let's check if today is past the expiration date.
       */
      let expires = new Date(cert.expires);
      let today = new Date();
      if(expires.getTime() <= today.getTime()) {
        /**
         * expiration date is in the past.  The cert is expired and therefore
         * invalid;
         */
        return false;
      } else {
        // cert expires eventually, but not today.  It's therefore valid.
        return true;
      }
    }
    // // original working statement
    // return ((cert && !cert.expires) || (cert && DateTime.fromISO(cert.expires) >= DateTime.local()) || (contentModule && !contentModule.certificateDuration))
  }

  // generate the "completion report" csv for the studies dashboard
  , generateCompletionReport(
      certsGroupedByUser
      , courseList
      , groupedStudyUsersBySiteNumber
      , orderedStudyUsersBySiteNumber
      , contentModuleMap
      , groupedUserModulesByUser = null
      , usersMap = null
      , studyRoleMap
    ) {
    
    let showPastCerts = false;
    /**
     * 1.15 - want option to show ALL certs, not just the most recent valid one
     * TODO: we need to redo all of this and have it happen on the backend
     */

    let includeLastLoginColumn = true;
    /**
     * 1.13 - not going to remove this, but also not going to deploy it to production just yet
     * I don't think that "onboarded" means what they think its means with sso additions,
     * for ex I think they want to use it as a proxy for "is this user active" but I don't think
     * it will ever be generated for someone who uses sso to login vs someone who got the invite email
     */
    // 1.21 - they say they want this - also including a "sso" column to try to solve above problem

    // 2.0 - replace onboarded with user boolean

    // let replaceRegex = /[&\/\\#,+()$~%.'":*?<>{}]/g; // all special characters
    let replaceRegex = /#/g; // all special characters
    /**
     * 1.13 - they had issues with special characters, so we removed them. then they had issues with 
     * special characters being removed, so we're removing removing them.
     * however, the particular offending character was the # exclusively, so instead of undoing 
     * the changes, we're going to keep them in case we need it more in the future and just remove
     * that particular character.
     */

    // const onboardEventsGroupedByUser = onboardEvents ? _.groupBy(onboardEvents, '_user') : {};

    // previously this was resetting the filters when you tried to export, resulting in the entire
    // unfiltered one being exported. but now it is magically working????

    let csv = "";
    // set header rows
    if(includeLastLoginColumn) {
      csv += "Site,Full Name,Email,User Status,First Invited,Deactivated,Role,Course,Module,Optional,Cert Status,Issued,Expires,Last Login,SSO Provider(s),SSO ID(s)\n"
    } else {
      csv += "Site,Full Name,Email,User Status,First Invited,Deactivated,Role,Course,Module,Optional,Cert Status,Issued,Expires\n"
    }

    // We'll build the csv in the same order that we build the completion report.
    orderedStudyUsersBySiteNumber.map(siteNumber => {
      const studyUserArray = groupedStudyUsersBySiteNumber[siteNumber].sort((a, b) => a._course < b._course ? -1 : 1)
      for(const studyUser of studyUserArray) {
        const nextStudyUser = studyUser;
        const studyRole = studyRoleMap[nextStudyUser._groupRole]; 
        
        // 1.12 - stop using the old analytics, but preserve backwards compatibility for the time being
        let nextUser;
        if(nextStudyUser.analytics && nextStudyUser.analytics.user) {
          nextUser = nextStudyUser.analytics.user
        } else {
          // use new userMap
          nextUser = usersMap[studyUser._user]
        }

        let hasLoggedIn = false;

        if(!nextUser) {
          // console.log("NO USER", nextStudyUser)
          continue
        } else {
          // Some users in the db have no first or last name.
          // Give those users empty strings to avoid "undefined" showing up on the report.
          if(!nextUser.firstName) {
            nextUser.firstName = ""
          }
          if(!nextUser.lastName) {
            nextUser.lastName = ""
          }

          // 2.0 - replace onboarded with user boolean
          // 2.5 - replace with hasPassword and lastLogin
          // get onboard event
          // hasLoggedIn = onboardEventsGroupedByUser[nextUser._id] && onboardEventsGroupedByUser[nextUser._id].length > 0
          hasLoggedIn = nextUser.lastLogin ? true : false


        }
        for(let i = 0; i < courseList.length; i++) {
          let course = courseList[i];
          let nextCourse = null;
          if(nextStudyUser._course == course._id) {
            // The user is assigned to this course.
            nextCourse = course;
          } else if(!nextStudyUser._course) {
            // The user has no assigned course, but they might still have certs, so we'll check this course just in case.
            nextCourse = course;
          }
          if(nextCourse) {
            let cmIds = nextCourse._contentModules
            const certsGroupedByCM = _.groupBy(certsGroupedByUser[nextUser._id], '_contentModule');
            for(const cmId of cmIds) {
              let nextCM = contentModuleMap[cmId];

              // Check if training module is optional for this study role
              const isOptional = studyRole._optionalModules.includes(cmId); 

              // Check for a module cert.
              let nextCert = null;
              let latestCert = null;
              const userCerts = certsGroupedByCM[cmId];
              if(userCerts && userCerts.length > 0) {
                nextCert = certUtils.findCompletedCert(userCerts);
                latestCert = certUtils.getLatestCert(userCerts)
              }
              if(latestCert && !showPastCerts) {
                // They have a cert for this CM. Get the status.
                if(!nextCert) {
                  // If nextCert is undefined continue with latestCert
                  nextCert = latestCert
                }
                const nextCertNotExpired = certUtils.checkIfValid(nextCert, nextCM); // 1.15 - i dont think this will do anything?
                let nextCertStatus = (
                  nextCert.status === 'completed' && !nextCertNotExpired ?
                  'Expired'
                  : nextCert.status === 'completed' ?
                  'Completed'
                  : nextCert.status === 'exempt' && !nextCertNotExpired ?
                  'Expired Exemption'
                  : nextCert.status === 'exempt' ?
                  'Exempt'
                  :
                  nextCert.status
                );
                // Add the next line to the CSV
                let nextLine = '';
                nextLine += (nextStudyUser.siteNumber == '' ? 'Unassigned' : nextStudyUser.siteNumber) + ','
                nextLine += '"' + nextUser.firstName + ' ' + nextUser.lastName + '",'
                nextLine += nextUser.username + ','
                nextLine += nextStudyUser.status + ','
                nextLine += new Date(nextStudyUser.created).toLocaleDateString() + ','
                nextLine += (nextStudyUser.status == 'deactivated' && nextStudyUser.dateRevoked ? new Date(nextStudyUser.dateRevoked).toLocaleDateString() : 'n/a') + ','
                nextLine += studyRole.role + ','
                nextLine += '"' + (nextCourse && nextCourse.title
                  ? `${nextCourse.title.replace(replaceRegex,' ')}` // just replace special characters
                  : 'n/a') + '",'
                nextLine += '"' + (nextCM && nextCM.title 
                  ? `${nextCM.title.replace(replaceRegex,' ')}` // just replace special characters
                  : 'n/a') + '",'
                nextLine += isOptional ? 'Yes' + ',' : 'No' + ','
                nextLine += nextCertStatus + ','
                nextLine += new Date(nextCert.issuedDate).toLocaleDateString() + ','
                nextLine += (!nextCert.expires ? 'n/a' : new Date(nextCert.expires).toLocaleDateString()) + ','
                
                if(includeLastLoginColumn) {
                  if(hasLoggedIn) {
                    nextLine += `${new Date(nextUser.lastLogin).toLocaleDateString()}`
                    nextLine += ","
                  } else {
                    nextLine += "n/a,"
                  }
                  if(nextUser.ssoProviders && nextUser.ssoProviders.length > 0) {
                    nextLine += `"${nextUser.ssoProviders.map(provider => provider.name)}"`
                    nextLine += ","
                    nextLine += `"${nextUser.ssoProviders.map(provider => provider.ssoId)}"`
                    nextLine += ","
                  } else {
                    nextLine += "no,"
                    nextLine += "n/a,"
                  }
                }
                // end line
                nextLine += '\n'
                csv += nextLine
              
              } else if(latestCert && showPastCerts) {
                // 1.15 - ok, what do now?
                // loop over ALL user certs
                for(let nextCert of userCerts) {
                  // Add the next line to the CSV
                  const nextCertNotExpired = certUtils.checkIfValid(nextCert, nextCM); // 1.15 - i dont think this will do anything?
                  let nextCertStatus = (
                    nextCert.status === 'completed' && !nextCertNotExpired ?
                    'Expired'
                    : nextCert.status === 'completed' ?
                    'Completed'
                    : nextCert.status === 'exempt' && !nextCertNotExpired ?
                    'Expired Exemption'
                    : nextCert.status === 'exempt' ?
                    'Exempt'
                    :
                    nextCert.status
                  );

                  let nextLine = '';
                  nextLine += (nextStudyUser.siteNumber == '' ? 'Unassigned' : nextStudyUser.siteNumber) + ','
                  nextLine += '"' + nextUser.firstName + ' ' + nextUser.lastName + '",'
                  nextLine += nextUser.username + ','
                  nextLine += nextStudyUser.status + ','
                  nextLine += new Date(nextStudyUser.created).toLocaleDateString() + ','
                  nextLine += (nextStudyUser.status == 'deactivated' && nextStudyUser.dateRevoked ? new Date(nextStudyUser.dateRevoked).toLocaleDateString() : 'n/a') + ','
                  nextLine += studyRole.role + ','
                  nextLine += '"' + (nextCourse && nextCourse.title
                    ? `${nextCourse.title.replace(replaceRegex,' ')}` // just replace special characters
                    : 'n/a') + '",'
                  nextLine += '"' + (nextCM && nextCM.title 
                    ? `${nextCM.title.replace(replaceRegex,' ')}` // just replace special characters
                    : 'n/a') + '",'
                  nextLine += isOptional ? 'Yes' + ',' : 'No' + ','
                  nextLine += nextCertStatus + ','
                  nextLine += new Date(nextCert.issuedDate).toLocaleDateString() + ','
                  nextLine += (!nextCert.expires ? 'n/a' : new Date(nextCert.expires).toLocaleDateString()) + ','
                  
                  if(includeLastLoginColumn) {
                    if(hasLoggedIn) {
                      nextLine += `${new Date(nextUser.lastLogin).toLocaleDateString()}`
                      nextLine += ","
                    } else {
                      nextLine += "n/a,"
                    }
                    if(nextUser.ssoProviders && nextUser.ssoProviders.length > 0) {
                      nextLine += `"${nextUser.ssoProviders.map(provider => provider.name)} "`
                      nextLine += ","
                      nextLine += `"${nextUser.ssoProviders.map(provider => provider.ssoId)} "`
                      nextLine += ","
                    } else {
                      nextLine += "no,"
                      nextLine += "n/a,"
                    }
                  }
                  // end line
                  nextLine += '\n'
                  csv += nextLine
                }
              } else if(nextStudyUser._course) {
                // No cert. But they are assigned to this course. Check for userModules.
                // 1.12 updates
                const modules = nextStudyUser.analytics ? nextStudyUser.analytics.modules : groupedUserModulesByUser[nextUser._id]
                let userModule = null;
                if(modules) {
                  for(const module of modules) {
                    if(module._contentModule == cmId) {
                      userModule = module
                      break
                    }
                  }
                }
                if(userModule) {
                  // console.log('HAS MODULES', nextStudyUser, userModule)
                  // User has started this CM. Add the next line to the CSV.
                  let nextLine = '';
                  nextLine += (nextStudyUser.siteNumber == '' ? 'Unassigned' : nextStudyUser.siteNumber) + ','
                  nextLine += '"' + nextUser.firstName + ' ' + nextUser.lastName + '",'
                  nextLine += nextUser.username + ','
                  nextLine += nextStudyUser.status + ','
                  nextLine += new Date(nextStudyUser.created).toLocaleDateString() + ','
                  nextLine += (nextStudyUser.status == 'deactivated' && nextStudyUser.dateRevoked ? new Date(nextStudyUser.dateRevoked).toLocaleDateString() : 'n/a') + ','
                  nextLine += studyRole.role + ','
                  nextLine += '"' + (nextCourse && nextCourse.title
                    ? `${nextCourse.title.replace(replaceRegex,' ')}` // just replace special characters
                    : 'n/a') + '",'
                  nextLine += '"' + (nextCM && nextCM.title 
                    ? `${nextCM.title.replace(replaceRegex,' ')}` // just replace special characters
                    : 'n/a') + '",'
                  nextLine += isOptional ? 'Yes' + ',' : 'No' + ','
                  // nextLine += '"' + (nextCourse && nextCourse.title ? nextCourse.title : 'n/a') + '",'
                  // nextLine += '"' + (nextCM && nextCM.title ? nextCM.title : 'n/a') + '",'
                  nextLine += 'In Progress' + ','
                  nextLine += 'n/a' + ','
                  nextLine += 'n/a' + ','

                  if(includeLastLoginColumn) {
                    if(hasLoggedIn) {
                      nextLine += `${new Date(nextUser.lastLogin).toLocaleDateString()}`
                      nextLine += ","
                    } else {
                      nextLine += "n/a,"
                    }
                    if(nextUser.ssoProviders && nextUser.ssoProviders.length > 0) {
                      nextLine += `"${nextUser.ssoProviders.map(provider => provider.name)} "`
                      nextLine += ","
                      nextLine += `"${nextUser.ssoProviders.map(provider => provider.ssoId)} "`
                      nextLine += ","
                    } else {
                      nextLine += "no,"
                      nextLine += "n/a,"
                    }
                  }
                  // end line
                  nextLine += '\n'
                  csv += nextLine
                } else {
                  // console.log('HAS NO MODULES', nextStudyUser)
                  // User has not started this CM. Add the next line to the CSV.
                  let nextLine = '';
                  nextLine += (nextStudyUser.siteNumber == '' ? 'Unassigned' : nextStudyUser.siteNumber) + ','
                  nextLine += '"' + nextUser.firstName + ' ' + nextUser.lastName + '",'
                  nextLine += nextUser.username + ','
                  nextLine += nextStudyUser.status + ','
                  nextLine += new Date(nextStudyUser.created).toLocaleDateString() + ','
                  nextLine += (nextStudyUser.status == 'deactivated' && nextStudyUser.dateRevoked ? new Date(nextStudyUser.dateRevoked).toLocaleDateString() : 'n/a') + ','
                  nextLine += studyRole.role + ','
                  nextLine += '"' + (nextCourse && nextCourse.title
                    ? `${nextCourse.title.replace(replaceRegex,' ')}` // just replace special characters
                    : 'n/a') + '",'
                  nextLine += '"' + (nextCM && nextCM.title 
                    ? `${nextCM.title.replace(replaceRegex,' ')}` // just replace special characters
                    : 'n/a') + '",'
                  nextLine += isOptional ? 'Yes' + ',' : 'No' + ','
                  // nextLine += '"' + (nextCourse && nextCourse.title ? nextCourse.title : 'n/a') + '",'
                  // nextLine += '"' + (nextCM && nextCM.title ? nextCM.title : 'n/a') + '",'
                  nextLine += nextStudyUser.status == 'deactivated' ? 'n/a' + ',' : 'Not Started' + ','
                  nextLine += 'n/a' + ','
                  nextLine += 'n/a' + ','
                  
                  if(includeLastLoginColumn) {
                    if(hasLoggedIn) {
                      nextLine += `${new Date(nextUser.lastLogin).toLocaleDateString()}`
                      nextLine += ","
                    } else {
                      nextLine += "n/a,"
                    }
                    if(nextUser.ssoProviders && nextUser.ssoProviders.length > 0) {
                      nextLine += `"${nextUser.ssoProviders.map(provider => provider.name)} "`
                      nextLine += ","
                      nextLine += `"${nextUser.ssoProviders.map(provider => provider.ssoId)} "`
                      nextLine += ","
                    } else {
                      nextLine += "no,"
                      nextLine += "n/a,"
                    }
                  }
                  // end line
                  nextLine += '\n'
                  csv += nextLine
                }
              } else if(!nextStudyUser._course && i === (courseList.length - 1)) {
                // We've looped through all courses and found no certs. They also have no assigned course. This means they've not started any training.
                let nextLine = '';
                nextLine += (nextStudyUser.siteNumber == '' ? 'Unassigned' : nextStudyUser.siteNumber) + ','
                nextLine += '"' + nextUser.firstName + ' ' + nextUser.lastName + '",'
                nextLine += nextUser.username + ','
                nextLine += nextStudyUser.status + ','
                nextLine += new Date(nextStudyUser.created).toLocaleDateString() + ','
                nextLine += (nextStudyUser.status == 'deactivated' && nextStudyUser.dateRevoked ? new Date(nextStudyUser.dateRevoked).toLocaleDateString() : 'n/a') + ','
                nextLine += studyRole.role + ','
                nextLine += nextStudyUser.status == 'deactivated' ? '"' + 'n/a' + '",' : '"' + 'Waiting for user to start' + '",'
                // nextLine += '"' + (nextCM && nextCM.title ? nextCM.title : 'n/a') + '",'
                nextLine += '"' + (nextCM && nextCM.title 
                  ? `${nextCM.title.replace(replaceRegex,' ')}` // just replace special characters
                  : 'n/a') + '",'
                nextLine += isOptional ? 'Yes' + ',' : 'No' + ','
                nextLine += nextStudyUser.status == 'deactivated' ? 'n/a' + ',' : 'Not Started' + ','
                nextLine += 'n/a' + ','
                nextLine += 'n/a' + ','
                
                if(includeLastLoginColumn) {
                  if(hasLoggedIn) {
                    nextLine += `${new Date(nextUser.lastLogin).toLocaleDateString()}`
                    nextLine += ","
                  } else {
                    nextLine += "n/a,"
                  }
                  if(nextUser.ssoProviders && nextUser.ssoProviders.length > 0) {
                    nextLine += `"${nextUser.ssoProviders.map(provider => provider.name)} "`
                    nextLine += ","
                    nextLine += `"${nextUser.ssoProviders.map(provider => provider.ssoId)} "`
                    nextLine += ","
                  } else {
                    nextLine += "no,"
                    nextLine += "n/a,"
                  }
                }
                // end line
                nextLine += '\n'
                csv += nextLine
              }
            }
          }
        }
      }
    });
    return csv;
  }
  , generateAggregateSurveyReport(
      studyUsers
      , surveyList
  ) {
    let csv = "";
    // set header rows
    csv += "Metric, Total\n"

    // We'll build the csv in the same order that we build the aggregate survey report.
    surveyList.map(survey => {
      let surveyEntries = {
        totalByMetric: 0
      };
      for(let studyUser of studyUsers) {
        // get totals for ALL modules of this StudyUsers 
        // semantics, but is that what the want here? that is what it did before. it could also total up EACH MODULE for a single user.
        if(studyUser.analytics.modules) {
          for(let module of studyUser.analytics.modules) {
            if(module.surveys && module.surveys[survey] && module.surveys[survey].total > 0) {
              surveyEntries.totalByMetric++;
            }
          }
        }
      }
      // Add the next line to the CSV
      let nextLine = '';
      nextLine += survey + ','
      nextLine += surveyEntries.totalByMetric
      // end line
      nextLine += '\n'
      csv += nextLine
    });
    return csv;
  }

  // generate the "training roles exclusion report"
  , generateRoleBasedExclusionReport(
    courses
    , studyRoles
    , contentModuleMap
  ) {
    // we have two header rows, course title and then all CM's for that course beneath it.
    let csv = "";
    // set header rows
    courses.map(course => {
      // start on the second column to match the ui.
      // csv +=  ", " + course.title;
      csv += `, ${course.title.replace(/[&\/\\#,+()$~%.'":*?<>{}]/g,' ')}` // just replace special characters
      // add a blank cell for each CM in this course so that we can align the course title with its CMs.
      course._contentModules.map((cmId, i) => {
        // we don't want to add a cell for the first one since it will be directly below the course title
        if(i > 0) {
          // add a blank cell for each CM.
          csv += ", " 
        }
      })
    })

    // Course headers done. Add a new line.
    csv += "\n";
    // the first row of the next column is the role header
    csv += "ROLE"
    courses.map(course => {
      // Add a cell for each CM title.
      course._contentModules.map(cmId => {
        const tm = contentModuleMap[cmId];
        // // if this has special characters that are invalid with csv, its very hard to escape alongwith the encodeUri
        // csv += ", " + tm.title;
        // csv += `, \"${tm.title}\"`
        csv += `, ${tm.title.replace(/[&\/\\#,+()$~%.'":*?<>{}]/g,' ')}` // just replace special characters
      })
    })
    // done with both header rows. Go to the next line.
    csv += "\n";
    // Now we need to loop through the courses and their CMs for each role.
    studyRoles.map(studyRole => {
      // the first column is the studyRole.role
      csv += studyRole.role;
      courses.map(course => {
        // Now add "excluded" or "-" for each CM.
        course._contentModules.map(cmId => {
          if(studyRole._excludedModules.includes(cmId)) {
            csv += ", Excluded"
          } else if(studyRole._optionalModules.includes(cmId)) {
            csv += ", Optional"
          } else {
            csv += ", -"
          }
        })
      })
      // Done with that role, move on to the next line.
      csv += "\n";
    })
    // Done with all roles.
    return csv;
  }
}

export default certUtils;



// WEBPACK FOOTER //
// global/utils/certUtils.js